import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import api from "../../api/axiosInstance";
import { UserListProps, UserRolesEnum } from "../../typings/users";
import UsersList from "./UserList";
import UserScreenHeader from "./UserScreenHeader";
import { PaginationProps } from "../../typings";
import { PAGINATION_VALUES } from "../../data/pagination";

const SEARCH_FIELDS = "name";

const Users = () => {
  const [users, setUsers] = useState<UserListProps[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationProps>(PAGINATION_VALUES);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<UserRolesEnum | "">("");

  const fetchUsers = useCallback(async (search: string, page: number, pageSize: number, role: UserRolesEnum | "") => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page_no: page,
        page_size: pageSize,
      };

      if (search.trim()) {
        params.search_str = search.trim();
        params.search_fields = SEARCH_FIELDS;
      }

      if (role) {
        params.role = role;
      }

      const response = await api.get("/users", { params });
      setUsers(response.data.data);
      setPagination(response.data.pagination);
      setError("");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch whenever the (already debounced) search term, role filter, or pagination changes
  useEffect(() => {
    fetchUsers(searchTerm, pagination.page_number, pagination.page_size, selectedRole);
  }, [searchTerm, selectedRole, pagination.page_number, pagination.page_size, fetchUsers]);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    // Reset to first page on new search
    setPagination((prev) => {
      if (prev.page_number === 1) return prev;
      return { ...prev, page_number: 1 };
    });
  }, []);

  const handleRoleChange = useCallback((role: UserRolesEnum | "") => {
    setSelectedRole(role);
    // Reset to first page on filter change
    setPagination((prev) => {
      if (prev.page_number === 1) return prev;
      return { ...prev, page_number: 1 };
    });
  }, []);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPagination((p) => ({ ...p, page_number: newPage + 1 })); // MUI is 0-based
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPagination((prev) => ({
        ...prev,
        page_number: 1,
        page_size: parseInt(event.target.value, 10),
      }));
    },
    []
  );

  // Only show a full-page error — never a full-page loading spinner
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header always stays rendered — search box never unmounts */}
      <UserScreenHeader
        onSearch={handleSearch}
        onRoleChange={handleRoleChange}
        selectedRole={selectedRole}
      />
      <UsersList
        users={users}
        pagination={pagination}
        loading={loading}
        searchTerm={searchTerm}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default Users;
